import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import useAuthStore from '../store/authStore';
import { academicService } from '../services/api';
import Sidebar from '../components/Sidebar';
import TopNav from '../components/TopNav';
import { useTranslation } from 'react-i18next';
import { StarDisplay } from '../components/StarRating';
import { Pagination } from '../components/ui';
import { useNotification } from '../contexts/NotificationContext';
import { useConfirm } from '../contexts/ConfirmContext';
import { getApiErrorMessage } from '../utils/apiErrors';

const COURSES_PAGE_SIZE = 9;

function Courses() {
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuthStore();
  const [modules, setModules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const [page, setPage] = useState(1);
  const [updatingPublicationId, setUpdatingPublicationId] = useState(null);

  const { t } = useTranslation();
  const { addNotification } = useNotification();
  const confirm = useConfirm();

  const isTeacher = user?.roles?.some(r => r.name.toLowerCase() === 'teacher');

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }

    const fetchModules = async () => {
      try {
        setLoading(true);
        // For authenticated users, fetch "my" modules (enrolled for students, owned for teachers)
        const response = await academicService.getMyModules();
        const data = response.data;
        const modulesList = Array.isArray(data) ? data : data.results || [];

        setModules(modulesList.map((module) => ({
          ...module,
          pending_count: module.pending_count || 0,
          _enrollment_status: module.enrollment_status?.toLowerCase() || null,
          completion_percentage: module.completion_percentage || 0,
        })));
      } catch (err) {
        console.error('Error fetching modules:', err);
        setModules([]);
      } finally {
        setLoading(false);
      }
    };

    fetchModules();
  }, [isAuthenticated, isTeacher, navigate]);

  useEffect(() => {
    setPage(1);
  }, [filter]);

  const handleDeleteModule = async (event, module) => {
    event.stopPropagation();
    if (!(await confirm(t('Delete {{name}}? This cannot be undone.', { name: module.name })))) return;
    try {
      await academicService.deleteModule(module.id);
      setModules((current) => current.filter((item) => item.id !== module.id));
      if (visibleModules.length === 1 && page > 1) setPage(page - 1);
      addNotification(t('Course deleted successfully!'), 'success');
    } catch (error) {
      addNotification(getApiErrorMessage(error, t, 'Failed to delete course'), 'error');
    }
  };

  const handleTogglePublication = async (event, module) => {
    event.stopPropagation();
    if (module.is_active && !(await confirm(t('Unpublish {{name}}? Students will no longer see this course.', { name: module.name })))) return;

    setUpdatingPublicationId(module.id);
    try {
      const response = await academicService.updateModule(module.id, { is_active: !module.is_active });
      const updated = response.data || response;
      setModules((current) => current.map((item) => (
        item.id === module.id ? { ...item, is_active: updated.is_active } : item
      )));
      addNotification(
        t(module.is_active ? 'Course unpublished successfully!' : 'Course published successfully!'),
        'success'
      );
    } catch (error) {
      addNotification(getApiErrorMessage(error, t, 'Failed to update course publication'), 'error');
    } finally {
      setUpdatingPublicationId(null);
    }
  };

  if (!user) {
    return <div className="min-h-screen flex items-center justify-center"><div className="animate-pulse text-primary-600">Loading...</div></div>;
  }

  
  // helper to compute visible modules by filter (for students)
  const filteredModules = (() => {
    if (isTeacher) return modules;
    if (filter === 'inprogress') return modules.filter(m => (m._enrollment_status || '') === 'active' && m.completion_percentage < 100);
    if (filter === 'completed') return modules.filter(m => m.completion_percentage >= 100);
    return modules;
  })();
  const visibleModules = filteredModules.slice((page - 1) * COURSES_PAGE_SIZE, page * COURSES_PAGE_SIZE);
  return (
    <div className="flex h-screen bg-gray-50">
      <Sidebar />
      <div className="flex-1 flex flex-col overflow-hidden">
        <TopNav title={isTeacher ? t('My Modules') : t('My Courses')} subtitle={isTeacher ? t('My Modules') : t('My Courses')} />
        
        <main className="flex-1 overflow-y-auto p-6">
          {isTeacher && modules.length > 0 && (
            <div className="flex justify-end mb-6">
              <button
                onClick={() => navigate('/modules/create')}
                className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition font-medium"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                {t('Create Module')}
              </button>
            </div>
          )}

          {/* Filter Tabs - only for students */}
          {!isTeacher && (
            <div className="mb-6 flex space-x-2 border-b border-gray-200">
              <button onClick={() => setFilter('all')} className={`px-4 py-2 font-medium ${filter === 'all' ? 'text-primary-600 border-b-2 border-primary-600' : 'text-gray-600 hover:text-primary-600'}`}>
                {t('Courses')}
              </button>
              <button onClick={() => setFilter('inprogress')} className={`px-4 py-2 font-medium ${filter === 'inprogress' ? 'text-primary-600 border-b-2 border-primary-600' : 'text-gray-600 hover:text-primary-600'}`}>
                {t('In Progress')}
              </button>
              <button onClick={() => setFilter('completed')} className={`px-4 py-2 font-medium ${filter === 'completed' ? 'text-primary-600 border-b-2 border-primary-600' : 'text-gray-600 hover:text-primary-600'}`}>
                {t('Completed')}
              </button>
            </div>
          )}

          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[1, 2, 3].map(i => (
                <div key={i} className="bg-white rounded-xl shadow-md p-6 animate-pulse">
                  <div className="h-32 bg-gray-200 rounded mb-4"></div>
                  <div className="h-4 bg-gray-200 rounded w-3/4 mb-2"></div>
                  <div className="h-4 bg-gray-200 rounded w-1/2"></div>
                </div>
              ))}
            </div>
          ) : modules.length === 0 ? (
            <div className="text-center py-16">
              <svg className="w-24 h-24 mx-auto text-gray-300 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
              </svg>
              <h3 className="text-xl font-semibold text-gray-700 mb-2">{t('No Courses Yet')}</h3>
              <p className="text-gray-500 mb-4">{t('Start your learning journey today')}</p>
              <button onClick={() => isTeacher ? navigate('/modules/create') : navigate('/store')} className="px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700">
                {isTeacher ? t('Create Module') : t('Browse Courses')}
              </button>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {visibleModules.map((module) => (
                <div key={module.id} onClick={() => navigate(`/modules/${module.id}`)} className="bg-white rounded-xl shadow-md overflow-hidden hover:shadow-xl transition cursor-pointer group">
                  {module.thumbnail ? (
                    <div
                      className="h-40 bg-cover bg-center flex items-center justify-center relative"
                      style={{ backgroundImage: `url(${module.thumbnail}${module.updated_at ? `?v=${new Date(module.updated_at).getTime()}` : `?v=${Date.now()}`})` }}
                    >
                      <div className="bg-black/10 w-full h-full absolute rounded-t-xl pointer-events-none" />
                      {module.pending_count > 0 && (
                        <button title={t('Pending enrollments')} onClick={(e) => { e.stopPropagation(); navigate(`/modules/${module.id}?tab=students`); }} className="absolute top-2 right-2 inline-flex items-center justify-center px-2 py-0.5 text-xs font-semibold text-white bg-red-600 rounded">{module.pending_count}</button>
                      )}
                    </div>
                  ) : (
                    <div className="h-40 bg-gradient-to-br from-primary-500 to-purple-600 flex items-center justify-center">
                      {module.pending_count > 0 && (
                        <button title={t('Pending enrollments')} onClick={(e) => { e.stopPropagation(); navigate(`/modules/${module.id}?tab=students`); }} className="absolute top-2 right-2 inline-flex items-center justify-center px-2 py-0.5 text-xs font-semibold text-white bg-red-600 rounded">{module.pending_count}</button>
                      )}
                      <span className="text-white text-4xl font-bold">{module.code?.substring(0, 3)}</span>
                    </div>
                  )}
                  <div className="p-6">
                    <div className="flex items-center justify-between mb-2 gap-2">
                      <div className="flex items-center gap-2 min-w-0">
                        <span className="text-xs font-semibold text-primary-600 bg-primary-50 px-2 py-1 rounded truncate">{module.code}</span>
                        {isTeacher && (
                          <span className={`text-xs font-semibold px-2 py-1 rounded ${module.is_active ? 'bg-green-50 text-green-700' : 'bg-amber-50 text-amber-700'}`}>
                            {module.is_active ? t('Published') : t('Draft')}
                          </span>
                        )}
                      </div>
                      <span className="text-xs text-gray-500">{module.grades?.[0]?.name || 'N/A'}</span>
                    </div>
                    <h3 className="text-lg font-bold text-gray-800 mb-2 group-hover:text-primary-600 transition">{module.name}</h3>
                    {module.average_rating != null && (
                      <div className="flex items-center gap-1.5 mb-2">
                        <StarDisplay value={module.average_rating} size="w-3.5 h-3.5" />
                        <span className="text-xs text-gray-500">
                          {Number(module.average_rating).toFixed(1)} ({module.ratings_count})
                        </span>
                      </div>
                    )}
                    {module.description && (
                      <p className="text-sm text-gray-600 mb-4 line-clamp-2">{module.description}</p>
                    )}
                    {!isTeacher && (
                      <div className="mb-4">
                        <div className="flex justify-between text-xs text-gray-500 mb-1">
                          <span>{module.completion_percentage >= 100 ? t('Completed') : t('In Progress')}</span>
                          <span>{module.completion_percentage}%</span>
                        </div>
                        <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
                          <div className={`h-full rounded-full ${module.completion_percentage >= 100 ? 'bg-green-500' : 'bg-primary-500'}`} style={{ width: `${module.completion_percentage}%` }} />
                        </div>
                      </div>
                    )}
                    <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                      <div className="flex items-center space-x-2 text-sm text-gray-500">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                        </svg>
                        <span>{t('{{count}} Lessons', { count: module.lessons_count || 0 })}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        {isTeacher && (
                          <>
                            <button
                              onClick={(event) => handleTogglePublication(event, module)}
                              disabled={updatingPublicationId === module.id}
                              className={`px-2.5 py-1.5 rounded-lg transition text-xs font-semibold disabled:opacity-60 ${module.is_active ? 'text-amber-700 hover:bg-amber-50' : 'text-green-700 bg-green-50 hover:bg-green-100'}`}
                            >
                              {updatingPublicationId === module.id
                                ? t('Updating...')
                                : t(module.is_active ? 'Unpublish' : 'Publish')}
                            </button>
                            <button
                              onClick={(event) => handleDeleteModule(event, module)}
                              className="p-2 text-red-500 hover:text-red-700 hover:bg-red-50 rounded-lg transition"
                              title={t('Delete')}
                              aria-label={t('Delete')}
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                              </svg>
                            </button>
                          </>
                        )}
                        <button className="text-primary-600 hover:text-primary-700 font-medium text-sm">
                          {t('View')} →
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
              </div>
              <Pagination
                page={page}
                count={filteredModules.length}
                pageSize={COURSES_PAGE_SIZE}
                onPageChange={setPage}
                className="mt-6 rounded-xl bg-white shadow-sm"
              />
            </>
          )}
        </main>
      </div>
    </div>
  );
}

export default Courses;
